import type { Anmerkung, EntwurfSegmentEintrag } from '@awf/shared';
import { useEffect, useRef, useState } from 'react';

/**
 * Signatur von `speichereEntwurf`/`loescheEntwurf` aus `FreigabeSeiteProps` -- hier definiert
 * statt dort importiert (F3, Whole-Branch-Review): `freigabe-page.tsx` importiert bereits aus
 * dieser Datei (`useEntwurfSchreiber`), ein Ruecktransport von dort haette einen Typzyklus
 * zwischen beiden Dateien ergeben. `freigabe-page.tsx` nutzt diese Typen jetzt seinerseits in
 * `FreigabeSeiteProps`, beide Seiten teilen sich dieselbe Definition.
 */
export type SpeichereEntwurfFn = (
  runId: string,
  text: string,
  runde: number,
  segmentierung: EntwurfSegmentEintrag[] | undefined,
  anmerkungen: Anmerkung[],
) => Promise<void>;
export type LoescheEntwurfFn = (runId: string, runde: number) => Promise<void>;

/**
 * Ein einzelner, noch nicht gestarteter Schreibauftrag zum Entwurf -- siehe `entwurfSchreiben`
 * weiter unten in dieser Datei. `still`: dieser Auftrag darf im Fehlerfall NICHT den sichtbaren
 * `entwurfFehler`-Hinweis setzen (fuer die stille Leerentwurf-Aufraeumung beim Laden UND
 * fuer `entwurfVerwerfen`, das sein Ergebnis ueber `melde` bekommt und selbst entscheidet,
 * welcher Hinweis dafuer erscheint -- siehe dort). `melde`: optionale Rueckmeldung an den
 * Aufrufer, ob DIESER Auftrag geklappt hat -- fuer Aufrufer, die (anders als Autosave/
 * Uebernehmen) ein eigenes Ergebnis brauchen, weil sie selbst kein `void` zurueckgeben.
 */
export type EntwurfSchreibergebnis = 'ok' | 'fehler';
export type EntwurfSchreibauftrag =
  | {
      art: 'speichern';
      runId: string;
      runde: number;
      text: string;
      // Ruling 34: die Segment-Zuordnung DIESES Textes zum Zeitpunkt dieses Auftrags --
      // siehe `segmentierungFuer` unten. Optional, weil sie ohne `struktur` (z. B. bei
      // einem Lauf-Typ ohne Struktur) gar nicht gebildet werden kann.
      segmentierung?: EntwurfSegmentEintrag[];
      anmerkungen: Anmerkung[];
      still?: boolean;
      melde?: (ergebnis: EntwurfSchreibergebnis) => void;
    }
  | {
      art: 'loeschen';
      runId: string;
      runde: number;
      still?: boolean;
      melde?: (ergebnis: EntwurfSchreibergebnis) => void;
    };

/**
 * Gate-Befund HOCH (Nacharbeit PR #38): derselbe Auftrag wie oben, ERGAENZT um den
 * Lauf-Bezug zum Zeitpunkt des EINREIHENS -- separat vom oeffentlichen Typ, damit kein
 * Aufrufer von `planeEntwurfSchreiben` ihn selbst mitgeben muss (oder vergessen kann).
 * Siehe dort fuer das Warum: der Bezug darf NICHT erst beim Start dieses Schreibvorgangs
 * gelesen werden, weil ein Auftrag hinter einem anderen wartet und der Lauf in der
 * Zwischenzeit wechseln kann.
 */
type EntwurfSchreibauftragEingereiht = EntwurfSchreibauftrag & { laufToken: number };

export interface EntwurfSchreiber {
  planeEntwurfSchreiben(auftrag: EntwurfSchreibauftrag): void;
  /** Leert den noch nicht gestarteten Auftrag (absenden, Ruling 24) und gibt ihn zurueck. */
  verwirfAusstehenden(): EntwurfSchreibauftrag | null;
  entwurfHinweis: { gespeichertAm: string } | null;
  setEntwurfHinweis(wert: { gespeichertAm: string } | null): void;
  entwurfFehler: boolean;
  setEntwurfFehler(wert: boolean): void;
  entwurfLoeschenFehler: boolean;
  setEntwurfLoeschenFehler(wert: boolean): void;
  entwurfWirdVerworfen: boolean;
  setEntwurfWirdVerworfen(wert: boolean): void;
}

export function useEntwurfSchreiber({
  speichereEntwurf,
  loescheEntwurf,
  laufToken,
  beiFehler,
}: {
  speichereEntwurf: SpeichereEntwurfFn;
  loescheEntwurf: LoescheEntwurfFn;
  laufToken: { readonly current: number };
  beiFehler: () => void;
}): EntwurfSchreiber {
  // Die Seite reicht bei jedem Render eine neue Funktion herein; die Schleife unten laeuft
  // aber ueber mehrere Renders hinweg und muss immer die aktuelle aufrufen.
  const beiFehlerRef = useRef(beiFehler);
  useEffect(() => {
    beiFehlerRef.current = beiFehler;
  });

  // Nur gesetzt, solange ein geladener Entwurf noch nicht verworfen wurde (Ruling 13): der
  // Lauf wird hier ueberhaupt nur behandelt, wenn er auf eine Freigabe wartet -- ein
  // Entwurf zu einem laengst entschiedenen Lauf erreicht diesen Zustand gar nicht erst.
  const [entwurfHinweis, setEntwurfHinweis] = useState<{ gespeichertAm: string } | null>(null);
  // Nicht an ein einzelnes Segment gebunden (Task-13-Review MITTEL-1): `segmentUebernehmen`
  // schliesst das Segment, BEVOR der Speicherversuch ueberhaupt startet -- ein Fehler, der
  // nur im (dann laengst geschlossenen) Segment angezeigt wuerde, erreicht niemanden. Wird
  // erst bei der naechsten ERFOLGREICHEN Speicherung wieder zurueckgesetzt, nicht beim
  // blossen Oeffnen eines Segments.
  const [entwurfFehler, setEntwurfFehler] = useState(false);
  // Fehler beim Verwerfen (HOCH-2): getrennt von `entwurfFehler`, weil beides gleichzeitig
  // auftreten und sich sonst gegenseitig ueberschreiben koennte.
  const [entwurfLoeschenFehler, setEntwurfLoeschenFehler] = useState(false);
  // Task-13-Re-Review NEU-8: ohne eigenen Sperrzustand blieb "Fassung aus n8n laden"
  // waehrend des laufenden Loeschens klickbar -- mehrfaches Klicken sendet dann mehrere
  // Anfragen (idempotent, aber unnoetig). Dasselbe Muster wie `laeuft` fuer "Absenden".
  const [entwurfWirdVerworfen, setEntwurfWirdVerworfen] = useState(false);

  // Neuaufsatz des Entwurfspfads (Task 13, Fix-Runde 11) -- nach dem Vorbild von
  // `ergo-content-factory` (`beitrags-flaeche.tsx`): Die fruehere Kette (`entwurfKette`,
  // `planeEntwurfKettenglied`, ein "eingefrorener" Versions-Zaehler je geplanter Aktion)
  // sicherte KETTENGLIEDER gegeneinander ab -- zehn Fix-Runden lang erzeugte das immer neue
  // Wettrennen, jede Reparatur eine Folgeluecke. Der Ersatz senkt die Zahl der gleichzeitig
  // moeglichen Schreiber stattdessen strukturell auf hoechstens einen:
  // - `entwurfLaeuft` bewacht GENAU EINEN laufenden PUT/DELETE.
  // - `entwurfAusstehend` haelt NUR den zuletzt gewuenschten Schreibauftrag (Tiefe 1, kein
  //   FIFO-Array) -- eine neue Aenderung ERSETZT eine noch nicht begonnene aeltere, statt
  //   sich dahinter einzureihen.
  // - `entwurfSchreiben` (unten) ist eine `while`-Schleife ueber `entwurfAusstehend`: Kommt
  //   waehrend eines laufenden Schreibvorgangs ein neuer Auftrag herein, zieht genau DIESE
  //   Schleife ihn im selben Durchlauf nach, statt dass ein zweiter Sender startet. Sie
  //   draint daneben `entwurfFremdlaufVerwerfen` (siehe dort) -- ein wartendes Verwerfen
  //   fuer einen inzwischen verlassenen Lauf bleibt damit ebenfalls garantiert erreichbar.
  // Bewusst KEIN serverseitiger Versionsvergleich -- der zuletzt geschriebene Auftrag
  // gewinnt (siehe der Kommentar an `entwurfSchreiben` unten fuer die damit bewusst in Kauf
  // genommene Grenze).
  const entwurfLaeuft = useRef(false);
  const entwurfAusstehend = useRef<EntwurfSchreibauftragEingereiht | null>(null);
  // Gate-Befund HOCH (Nacharbeit PR #38, zweite Runde): `entwurfAusstehend` bleibt Tiefe 1
  // -- ABER ein dort bereits wartendes Verwerfen (siehe `planeEntwurfSchreiben` unten) darf
  // nur INNERHALB DESSELBEN Laufs blockieren/verdraengt werden. Gehoert der neu eingereihte
  // Auftrag zu einem ANDEREN Lauf, hat das wartende Verwerfen mit ihm nichts zu tun: Es wird
  // HIERHIN ausgelagert (statt im allgemeinen Platz zu verbleiben, wo es der neue Auftrag
  // sonst ueberschreiben wuerde), damit `entwurfSchreiben` es beim naechsten freien
  // Durchlauf trotzdem noch abarbeitet -- sein `melde` bekommt so in jedem Fall eine
  // Antwort, auch wenn der Lauf inzwischen gewechselt hat. Ein FIFO-Array, kein Tiefe-1-
  // Platz: mehrere Laufwechsel hintereinander koennen mehrere wartende Verwerfen-Auftraege
  // fuer verschiedene, laengst verlassene Laeufe anhaeufen, bevor der Mutex wieder frei wird.
  const entwurfFremdlaufVerwerfen = useRef<EntwurfSchreibauftragEingereiht[]>([]);

  /**
   * Der einzige Ort, an dem ein PUT/DELETE zum Entwurf tatsaechlich losgeschickt wird --
   * OHNE Ausnahme: auch `entwurfVerwerfen` (unten) plant sein `DELETE` nur ueber
   * `planeEntwurfSchreiben` und laeuft dadurch durch GENAU DIESE Schleife wie jeder andere
   * Schreibweg. Eine fruehere Fassung liess `entwurfVerwerfen` sein `DELETE` direkt AUSSERHALB
   * dieses Mutex verschicken (es setzte `entwurfLaeuft` selbst auf `true`/`false`) -- ein
   * WAEHREND dessen fuer einen inzwischen gewechselten Lauf neu geplanter Auftrag reihte sich
   * zwar brav in `entwurfAusstehend` ein, wurde danach aber nie gestartet: das direkte
   * `finally` von `entwurfVerwerfen` setzte `entwurfLaeuft` zurueck, OHNE den wartenden Auftrag
   * abzuholen (Gate-Befund HOCH). Wer `entwurfLaeuft` setzt, muss auch dafuer sorgen, dass ein
   * dabei entstandener `entwurfAusstehend`-Auftrag noch gestartet wird -- ausnahmslos, deshalb
   * jetzt keine zweite Stelle mehr, die das selbst uebernehmen muesste.
   *
   * Startet nur, wenn gerade KEIN anderer Schreibvorgang laeuft (`entwurfLaeuft`); laeuft
   * bereits einer, uebernimmt DESSEN Schleife den inzwischen neu gesetzten
   * `entwurfAusstehend`-Auftrag automatisch (naechste Iteration) -- ein zweiter Aufruf hier
   * haette in diesem Fall nichts mehr zu tun. Genau das ist auch die Wartelogik, die frueher
   * `entwurfVerwerfen` separat ueber `entwurfLaufendesVersprechen` nachbilden musste: ein
   * bereits laufender Schreibvorgang wird nie ueberholt, weil ein neuer Auftrag IMMER nur
   * `entwurfAusstehend` setzt, nie einen zweiten Sender startet.
   *
   * GRENZE, BEWUSST IN KAUF GENOMMEN (Punkt E des Auftrags): Es gibt keinen serverseitigen
   * Versionsvergleich. Zwei gleichzeitig geoeffnete Tabs auf demselben Lauf ueberschreiben
   * sich beim Entwurf gegenseitig -- wer zuletzt schreibt, gewinnt, ohne dass der jeweils
   * andere Tab das bemerkt. Konkret schliesst das EIN: ein `DELETE` (etwa "Fassung aus n8n
   * laden" in Tab A) erzeugt selbst keinen Versionsstand -- trifft danach noch ein
   * verzoegertes `PUT` derselben Runde aus Tab B ein (ein laengst abgeschickter Autosave,
   * der nur langsam beim Server ankommt), legt es den gerade verworfenen Entwurf einfach
   * wieder an. Weder die Runden- noch die `entschiedeneRunde`-Pruefung in
   * `auftraege.ts`/`runs-route.ts` schuetzen davor, weil beide nur eine INHALTLICH
   * ueberholte Schreibung erkennen, keine zeitlich verzoegerte NACH einem expliziten
   * Verwerfen derselben Runde. Das ist dieselbe Inkaufnahme wie im Vorbild
   * (`beitrags-flaeche.tsx`): eine echte Konflikterkennung (etwa ein Versionsfeld, das der
   * Server bei jedem Schreiben prueft und ablehnt) waere ein eigenes Vorhaben, kein Bestandteil
   * dieses Neuaufsatzes. Die vorherige Kette hat dieses Risiko NICHT verringert -- sie
   * serialisierte nur die Schreibvorgaenge DIESES EINEN Tabs untereinander, ein zweiter Tab
   * lief davon vollkommen unberuehrt.
   */
  function entwurfSchreiben() {
    if (entwurfLaeuft.current) return;
    entwurfLaeuft.current = true;
    void (async () => {
      try {
        while (entwurfAusstehend.current || entwurfFremdlaufVerwerfen.current.length > 0) {
          // Ausgelagerte Verwerfen-Auftraege ZUERST (siehe `entwurfFremdlaufVerwerfen` oben):
          // sie warten bereits seit einem verlassenen Lauf auf ihr `melde` -- der allgemeine
          // Platz `entwurfAusstehend` (jeweils der zuletzt gewuenschte Auftrag) darf sie
          // deshalb nicht weiter aufschieben.
          const auftrag = entwurfFremdlaufVerwerfen.current.shift() ?? entwurfAusstehend.current!;
          if (auftrag === entwurfAusstehend.current) entwurfAusstehend.current = null;
          // Gate-Befund HOCH (Nacharbeit PR #38): NICHT mehr hier per `laufToken.current`
          // frisch gelesen -- dieser Auftrag kann bereits eine Weile in `entwurfAusstehend`
          // GEWARTET haben (hinter einem noch laufenden anderen Schreibvorgang), und in
          // dieser Wartezeit kann der Lauf laengst gewechselt haben. Ein an DIESER Stelle
          // gelesener Wert waere dann IMMER der NEUE Token, der Vergleich unten also
          // wirkungslos -- ein veralteter Auftrag fuer den verlassenen Lauf gaelte
          // faelschlich als Auftrag des neuen. `auftrag.laufToken` traegt stattdessen den
          // Bezug vom EINREIHEN (siehe `planeEntwurfSchreiben`), der einzige Zeitpunkt, an
          // dem "welcher Lauf ist gemeint" tatsaechlich eindeutig ist.
          const meinToken = auftrag.laufToken;
          try {
            if (auftrag.art === 'loeschen') {
              await loescheEntwurf(auftrag.runId, auftrag.runde);
            } else {
              await speichereEntwurf(
                auftrag.runId,
                auftrag.text,
                auftrag.runde,
                auftrag.segmentierung,
                auftrag.anmerkungen,
              );
            }
            // `still`: die Leerentwurf-Aufraeumung beim Laden UND `entwurfVerwerfen` haben
            // KEINEN eigenen, sichtbaren `entwurfFehler`-Zustand -- Ersteres bleibt wie bisher
            // unbemerkt (Task-13-Re-Review NEU-11), Letzteres meldet sein Ergebnis stattdessen
            // ueber `melde` an einen EIGENEN Hinweis (`entwurfLoeschenFehler`, siehe dort).
            if (!auftrag.still && laufToken.current === meinToken) setEntwurfFehler(false);
            // Gate-Befund MITTEL: `entwurfHinweis` (Hinweisband + "Fassung aus n8n laden")
            // folgte bisher NUR einem beim Laden vorgefundenen Entwurf -- ein per Autosave
            // ERST in dieser Sitzung entstandener Zwischenstand blieb dadurch unsichtbar und
            // liess sich nicht mehr verwerfen, obwohl er serverseitig bereits liegt. Jeder
            // erfolgreiche Schreibvorgang zieht den Hinweis deshalb nach -- unabhaengig von
            // `still`, das nur die `entwurfFehler`-Sichtbarkeit betrifft: ein serverseitig
            // erfolgreich geschriebener/geloeschter Entwurf ist auch dann real. `loeschen`
            // raeumt den Hinweis symmetrisch wieder ab (deckt neben `entwurfVerwerfen` auch
            // die Abbrechen-Korrektur in `segmentAbbrechen` ab, deren `loeschen`-Zweig den
            // Hinweis sonst faelschlich stehen liesse, obwohl nichts mehr in Firestore liegt).
            if (laufToken.current === meinToken) {
              setEntwurfHinweis(
                auftrag.art === 'speichern' ? { gespeichertAm: new Date().toISOString() } : null,
              );
            }
            auftrag.melde?.('ok');
          } catch {
            if (!auftrag.still && laufToken.current === meinToken) {
              setEntwurfFehler(true);
              beiFehlerRef.current();
            }
            auftrag.melde?.('fehler');
          }
        }
      } finally {
        entwurfLaeuft.current = false;
      }
    })();
  }

  // Setzt/ersetzt den ausstehenden Schreibauftrag und stoesst den Mutex an -- der EINZIGE
  // Einstiegspunkt fuer jeden Schreibweg zum Entwurf: Autosave, Uebernehmen, die
  // Abbrechen-Korrektur UND `entwurfVerwerfen` (dessen eigenes Ergebnis holt sich `melde`).
  //
  // Gate-Befund HOCH (Nacharbeit PR #38): "der zuletzt gewuenschte Auftrag ersetzt einen
  // noch nicht begonnenen aelteren" ist die richtige Regel fuer einen AUTOSAVE -- der
  // neueste Stand macht jeden aelteren gegenstandslos. Fuer ein bereits wartendes
  // VERWERFEN (das einzige `loeschen` mit `melde`, siehe `entwurfVerwerfen`) gilt sie NUR
  // INNERHALB DESSELBEN LAUFS: eine bewusste Aktion mit einem auf ihr Ergebnis wartenden
  // Aufrufer wird nicht dadurch hinfaellig, dass IM SELBEN LAUF danach noch etwas anderes
  // geschrieben werden soll -- im Gegenteil, ein solcher Folgeauftrag ist selbst
  // gegenstandslos, weil der Entwurf gleich verschwindet.
  //
  // Gate-Befund HOCH (Nacharbeit PR #38, zweite Runde): Diese Regel pauschal auf JEDEN
  // Folgeauftrag anzuwenden -- unabhaengig vom Lauf -- war zu weitgehend: Nach einem
  // Laufwechsel hat das wartende Verwerfen mit einem Auftrag des NEUEN Laufs nichts mehr zu
  // tun. Es darf ihn weder verdraengen (er wuerde sonst den einzigen Platz in
  // `entwurfAusstehend` behalten und der neue Auftrag ginge verloren) noch blockieren (er
  // wuerde sonst -- als Autosave -- still verworfen, oder -- als eigenes Verwerfen des
  // neuen Laufs -- selbst nie ein `melde` bekommen). Der Lauf-Vergleich unten entscheidet
  // das: gehoert das wartende Verwerfen zum AKTUELLEN Lauf, bleibt es stehen und der neue
  // Auftrag faellt (wie oben beschrieben) still weg; gehoert es zu einem VERLASSENEN Lauf,
  // wandert es nach `entwurfFremdlaufVerwerfen` (siehe dort) und der neue Auftrag bekommt
  // den allgemeinen Platz -- beide werden dadurch garantiert noch verarbeitet, auch wenn
  // ZWEI verschiedene Laeufe gleichzeitig ein wartendes `melde` haben sollten.
  function planeEntwurfSchreiben(auftrag: EntwurfSchreibauftrag): void {
    const wartend = entwurfAusstehend.current;
    if (wartend?.art === 'loeschen' && wartend.melde) {
      if (wartend.laufToken === laufToken.current) return;
      entwurfFremdlaufVerwerfen.current.push(wartend);
    }
    // Gate-Befund HOCH (Nacharbeit PR #38): der Lauf-Bezug wird HIER, beim Einreihen,
    // erfasst -- siehe `EntwurfSchreibauftragEingereiht` und der Kommentar in
    // `entwurfSchreiben` fuer das Warum.
    entwurfAusstehend.current = { ...auftrag, laufToken: laufToken.current };
    entwurfSchreiben();
  }

  function verwirfAusstehenden(): EntwurfSchreibauftrag | null {
    const auftrag = entwurfAusstehend.current;
    entwurfAusstehend.current = null;
    return auftrag;
  }

  return {
    planeEntwurfSchreiben,
    verwirfAusstehenden,
    entwurfHinweis,
    setEntwurfHinweis,
    entwurfFehler,
    setEntwurfFehler,
    entwurfLoeschenFehler,
    setEntwurfLoeschenFehler,
    entwurfWirdVerworfen,
    setEntwurfWirdVerworfen,
  };
}
